import { FormProvider } from 'react-hook-form';
import { z } from 'zod';
import {
  createCardCreditBody,
  getListCardCreditsQueryKey,
  useCreateCardCredit,
  useValidateCardCreditSerial,
} from '@/api/trafikportalen';
import { SelectedDepartment } from '@/types/selectedDepartment';
import { TravellerWithCollectionMembershipsDTO } from '@/api/trafikportalen';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from '@/hooks/useToast';
import { FormElement } from '../FormElement';
import { FormSection } from '../FormSection';
import { DepartmentPicker } from '../DepartmentPicker';
import Input from '../core/Input';
import Button from '../core/Button';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { OrganizationCreditInfo } from '../OrganizationCreditInfo';
import { trimData } from '@/helpers/trimData';
import { Spinner } from '../core/Spinner';
import { IconCheck } from '@/icons/CoreIcons';
import { useOrgCreditInfo } from '@/hooks/useOrgCreditInfo';
import { TravellerCreditInfo } from '../TravellerCreditInfo';
import { hasDepartmentWriteAccess } from '@/helpers/departmentPermissions';
import { useGetDepartmentsList } from '@/api/trafikportalen';
import { useTenantID } from '@/hooks/useTenantID';
import { getTenantCardCreditLimit } from '@/constants/tenantCardCreditLimits';
import { cn } from '@/helpers/classnames';

interface CreditCardCreateFormProps {
  traveller: TravellerWithCollectionMembershipsDTO;
  onOpenChange: (open: boolean) => void;
}

type FormView = 'details' | 'departmentPicker';

export const CreditCardCreateForm = ({
  traveller,
  onOpenChange,
}: CreditCardCreateFormProps) => {
  const { formatMessage, locale } = useIntl();
  const queryClient = useQueryClient();
  const customErrorMap = useCustomErrorMap;
  const createCardCredit = useCreateCardCredit();
  const { remainingCredit, hasNoCreditLimit } = useOrgCreditInfo();
  const { data: allDepartments } = useGetDepartmentsList();
  const tenantId = useTenantID();
  const tenantMaxCreditLimit = getTenantCardCreditLimit(tenantId);

  const [formattedCardSerial, setFormattedCardSerial] = useState('');
  const [cardSerial, setCardSerial] = useState<string | null>(null);
  const [isCardValid, setIsCardValid] = useState(false);

  const [currentView, setCurrentView] = useState<FormView>('details');
  const [selectedDepartment, setSelectedDepartment] =
    useState<SelectedDepartment | null>(null);

  const allowedDepartments = useMemo(() => {
    const allowedDepartments: string[] = [];

    traveller.memberships.forEach((membership) => {
      if (
        membership.active &&
        hasDepartmentWriteAccess(membership.department_id)
      ) {
        allowedDepartments.push(membership.department_id);
      }
    });

    return [...new Set(allowedDepartments)];
  }, [traveller.memberships]);

  const schema = createCardCreditBody.and(
    z.object({
      credit: z.number().gte(0, {
        message: formatMessage({ defaultMessage: 'Kredit måste vara minst 0' }),
      }),
    }),
  );

  type CreateAllpayUserFormType = z.infer<typeof schema>;

  const customResolver = zodResolver(schema, {
    errorMap: customErrorMap(),
  });

  const methods = useForm<CreateAllpayUserFormType>({
    defaultValues: {
      organization_id: traveller.traveller.organization_id,
      card_serial: '',
      department_id: '',
      credit: 0,
      collection_member_id: '',
    },
    resolver: customResolver,
    mode: 'onChange',
  });

  useEffect(() => {
    if (allDepartments && allowedDepartments.length > 0) {
      const department = allDepartments.find(
        (department) => department.id === allowedDepartments[0],
      );
      if (department) {
        setSelectedDepartment(department);
        // Set form values when department is automatically selected
        const collectionMember = traveller.memberships.find(
          (membership) => membership.department_id === department.id,
        );
        if (collectionMember) {
          methods.setValue('department_id', department.id);
          methods.setValue(
            'collection_member_id',
            collectionMember.collection_member_id,
          );
          methods.trigger();
        }
      }
    }
  }, [allowedDepartments, allDepartments, traveller.memberships, methods]);

  const [creditString, setCreditString] = useState('');

  const creditError = useMemo(() => {
    if (!creditString) {
      return '';
    }
    const credit = parseInt(creditString);
    if (isNaN(credit)) {
      return formatMessage({ defaultMessage: 'Ogiltigt värde' });
    }
    if (credit < 0) {
      return formatMessage({
        defaultMessage: 'Kreditgränsen kan inte vara negativ',
      });
    }
    if (credit > tenantMaxCreditLimit) {
      return formatMessage(
        {
          defaultMessage: 'Kreditgränsen kan inte överstiga {maxLimit} SEK',
        },
        { maxLimit: tenantMaxCreditLimit.toLocaleString(locale) },
      );
    }
    if (!hasNoCreditLimit && credit > remainingCredit) {
      return formatMessage({
        defaultMessage:
          'Kreditgränsen kan inte överstiga organisationens kvarvarande kredit',
      });
    }
    return methods.formState.errors.credit?.message;
  }, [
    creditString,
    formatMessage,
    methods.formState.errors.credit?.message,
    remainingCredit,
    hasNoCreditLimit,
    tenantMaxCreditLimit,
    locale,
  ]);

  const {
    data: cardClaimable,
    error: cardSerialError,
    isFetching,
  } = useValidateCardCreditSerial(cardSerial ?? '', {
    query: {
      enabled: !!cardSerial && cardSerial.length === 16,
      // Unknown or unregistered cards are a validation answer, shown in the form.
      meta: { expectedErrorStatuses: [404] },
    },
  });

  const cancelEdit = useCallback(() => {
    methods.reset();
    setIsCardValid(false);
    onOpenChange?.(false);
  }, [methods, onOpenChange]);

  const handleDepartmentSelect = useCallback(
    (department: SelectedDepartment) => {
      const collectionMember = traveller.memberships.find(
        (membership) => membership.department_id === department.id,
      );
      if (!collectionMember) {
        return;
      }
      setSelectedDepartment(department);
      setCurrentView('details');
      methods.setValue('department_id', department.id);
      methods.setValue(
        'collection_member_id',
        collectionMember.collection_member_id,
      );
      methods.trigger();
    },
    [methods, traveller.memberships],
  );

  const onSubmit: SubmitHandler<CreateAllpayUserFormType> = useCallback(
    (data) => {
      createCardCredit.mutate(
        {
          data: trimData(data),
        },
        {
          onSuccess: async () => {
            toast.success(
              {
                title: formatMessage({
                  defaultMessage: 'Kredit på kort uppdaterad',
                }),
              },
              { autoClose: 3000 },
            );

            await queryClient.invalidateQueries({
              queryKey: getListCardCreditsQueryKey(),
            });

            cancelEdit();
          },
          onError: (error) => {
            console.error('Error: ', error);
            toast.error(
              {
                title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
                description: error.message,
              },
              { autoClose: 3000 },
            );
            return;
          },
        },
      );
    },
    [createCardCredit, formatMessage, queryClient, cancelEdit],
  );

  useEffect(() => {
    if (isFetching) return;

    if (!cardSerial || cardSerial.length !== 16) {
      return;
    }

    if (cardSerialError || !cardClaimable?.claimable) {
      setIsCardValid(false);
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Kortet är inte giltigt för tjänsteresa på kort.',
          }),
        },
        { autoClose: 3000 },
      );
      return;
    }

    methods.setValue('card_serial', cardSerial, {
      shouldValidate: true,
    });
    setIsCardValid(true);
  }, [
    isFetching,
    cardSerialError,
    formatMessage,
    cardSerial,
    methods,
    cardClaimable,
  ]);

  if (currentView === 'departmentPicker') {
    return (
      <div className="flex flex-col items-center gap-2">
        <DepartmentPicker
          title={<FormattedMessage defaultMessage="Välj betalande avdelning" />}
          suggestedDepartmentId={selectedDepartment?.id}
          onSelect={handleDepartmentSelect}
          enabledDepartments={allowedDepartments}
        />
        <p className="text-text-700">
          <FormattedMessage defaultMessage="Resenären måste vara medlem i avdelningen som ska betala för krediten." />
        </p>
        <Button variant="ghost" onClick={() => setCurrentView('details')}>
          <FormattedMessage defaultMessage="Avbryt" />
        </Button>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4">
          <OrganizationCreditInfo includeTitle={false} />
          <TravellerCreditInfo traveller={traveller} />
          <FormSection editMode={true}>
            <p className="text-text-900">
              <FormattedMessage defaultMessage="Kort som nyttjas för tjänsteresa på kort måste vara helt 'blanka' kort. De får inte tillhöra någon i resesystemet eller just nu nyttjas som resekort eller tjänsteresa på kort på någon resenär i detta systemet." />
            </p>
            {!isCardValid ? (
              <FormElement
                id="card_serial"
                label={<FormattedMessage defaultMessage="Kortnummer" />}
                input={
                  <Input
                    id="card_serial"
                    type="text"
                    placeholder={formatMessage({
                      defaultMessage: '16 siffror',
                    })}
                    value={formattedCardSerial}
                    onChange={(e) => {
                      let value = e.target.value;

                      if (value !== '0') {
                        // Remove all non-numeric characters
                        value = value.replace(/\D/g, '');
                      }

                      value = value.slice(0, 16);

                      // Format the value with spaces every 4 digits
                      const formattedValue = value.replace(
                        /(\d{4})(?=\d)/g,
                        '$1 ',
                      );

                      setFormattedCardSerial(formattedValue);
                      setCardSerial(value);

                      methods.setValue('card_serial', value);
                    }}
                    endAdornment={
                      isFetching ? <Spinner className="size-4" /> : null
                    }
                    error={
                      cardSerialError?.status === 404
                        ? formatMessage({
                            defaultMessage:
                              'Kortet är inte giltigt som resekort',
                          })
                        : ''
                    }
                  />
                }
              />
            ) : (
              <FormElement
                label={<FormattedMessage defaultMessage="Valt kortnummer" />}
                input={
                  <div className="flex w-full justify-between">
                    {formattedCardSerial}
                    <IconCheck />
                  </div>
                }
              />
            )}
            <FormElement
              id="credit"
              label={<FormattedMessage defaultMessage="Kredit" />}
              input={
                <Input
                  id="credit"
                  type="text"
                  value={creditString}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (value !== '0') {
                      value = value.replace(/^0+/, '');
                    }
                    setCreditString(value);
                    const creditValue = parseInt(value);
                    if (!isNaN(creditValue) && creditValue >= 0) {
                      methods.setValue('credit', creditValue);
                    } else if (value === '' || value === '0') {
                      methods.setValue('credit', 0);
                    }
                    methods.trigger('credit');
                  }}
                  error={creditError}
                  helperTextReserveSpace={true}
                  onBlur={() => {
                    const currentCredit = methods.getValues('credit');
                    setCreditString(currentCredit.toString());
                  }}
                  endAdornment={<FormattedMessage defaultMessage="SEK" />}
                />
              }
            />
            <FormElement
              label={<FormattedMessage defaultMessage="Betalande avdelning" />}
              button={
                <Button
                  className={cn(
                    'w-full',
                    selectedDepartment?.name &&
                      'justify-start truncate text-left',
                  )}
                  onClick={() => setCurrentView('departmentPicker')}
                >
                  <p className="truncate">
                    {selectedDepartment?.name ?? 'Välj en avdelning'}
                  </p>
                </Button>
              }
              input={null}
            />
            <p className="mt-3 text-text-800">
              <FormattedMessage defaultMessage="Kort som nyttjas för tjänsteresa på kort måste vara helt 'blanka' kort. De får inte tillhöra någon profil i resesystemet." />
            </p>
          </FormSection>
        </div>
        <div className="mt-4 flex w-full flex-row justify-end gap-2">
          <Button onClick={cancelEdit} variant="ghost">
            <FormattedMessage defaultMessage="Avbryt" />
          </Button>
          <Button
            startIcon={<IconCheck />}
            variant="filled"
            type="submit"
            disabled={
              !isCardValid ||
              !selectedDepartment ||
              !!creditError ||
              !methods.getValues('department_id') ||
              !methods.getValues('card_serial') ||
              methods.getValues('credit') < 0 ||
              methods.getValues('credit') === undefined ||
              methods.getValues('credit') === null
            }
            loading={createCardCredit.isPending}
          >
            <FormattedMessage defaultMessage="Lägg till kort" />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
